import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {Container as Button} from 'components/Button';
import {ButtonVideo, Prices} from '../components';
import {BlockTitle, ButtonRounded} from '../components/styled';
import {getDiscountPercent, getFormattedDate} from '../helpers';
import {CoursePromoContext} from '../context';
import {DateFormat, ICoursePromoContext} from '../types';

const Container = styled.div`
  padding: 60px 52px;
  width: 100%;
  max-width: 626px;
  background-color: ${({theme}) => `
    ${transparentize(.15, theme.variables.accentBackgroundColor)};
  `};

  border: 1px solid ${({theme}) => `
    ${transparentize(.95, theme.colors.white)};
  `};
  border-radius: 10px;
  box-sizing: border-box;
  box-shadow: 5px 5px 60px rgba(24, 24, 24, 0.8);
  
  backdrop-filter: blur(5px);
  
  button {
    background-color: transparent;
    &:hover {
      background-color: transparent;
    }
  }
  
  @media screen and (max-width: 640px) {
    margin: 0 auto;
    padding: 32px 16px;
  }
`;

const Duration = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  margin-bottom: 20px;
`;

const Title = styled(BlockTitle)`
  max-width: 496px;
`;

const Description = styled.div`
  margin-top: 14px;
  margin-bottom: 35px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

const Discount = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  max-width: max-content;
  border-radius: 100px;
  background-color: #FFE605;
  
  color: ${({theme}) => `
    ${theme.variables.accentBackgroundColor}
  `};
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  
  &::before {
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0)'%3E%3Cpath d='M15.2643 8.00057L15.9663 6.16969C16.0433 5.9677 15.9833 5.73872 15.8143 5.60173L14.2924 4.36781L13.9844 2.43094C13.9504 2.21696 13.7824 2.04997 13.5684 2.01597L11.6316 1.70799L10.3987 0.185095C10.2627 0.0161068 10.0287 -0.0438892 9.8317 0.0331056L7.99982 0.736058L6.16895 0.0341055C5.96596 -0.0438892 5.73898 0.0181066 5.60199 0.186095L4.36807 1.70899L2.4312 2.01697C2.21822 2.05097 2.05023 2.21896 2.01623 2.43194L1.70825 4.36881L0.185355 5.60273C0.0173667 5.73872 -0.0436291 5.9677 0.0333656 6.16969L0.735318 8.00057L0.0333656 9.83144C-0.0446291 10.0334 0.0173667 10.2624 0.185355 10.3984L1.70825 11.6313L2.01623 13.5682C2.05023 13.7822 2.21722 13.9502 2.4312 13.9842L4.36807 14.2921L5.60199 15.814C5.73898 15.984 5.96796 16.044 6.16995 15.966L7.99982 15.2651L9.8307 15.967C9.88869 15.989 9.94869 16 10.0097 16C10.1567 16 10.3017 15.935 10.3987 15.814L11.6316 14.2921L13.5684 13.9842C13.7824 13.9502 13.9504 13.7822 13.9844 13.5682L14.2924 11.6313L15.8143 10.3984C15.9833 10.2614 16.0433 10.0334 15.9663 9.83144L15.2643 8.00057Z' fill='%2329292B'/%3E%3Cpath d='M6.4999 7.00077C5.67295 7.00077 5 6.32782 5 5.50087C5 4.67393 5.67295 4.00098 6.4999 4.00098C7.32684 4.00098 7.99979 4.67393 7.99979 5.50087C7.99979 6.32782 7.32684 7.00077 6.4999 7.00077ZM6.4999 5.00091C6.22392 5.00091 5.99993 5.22489 5.99993 5.50087C5.99993 5.77685 6.22392 6.00084 6.4999 6.00084C6.77588 6.00084 6.99986 5.77685 6.99986 5.50087C6.99986 5.22489 6.77588 5.00091 6.4999 5.00091Z' fill='white'/%3E%3Cpath d='M9.4999 11.9998C8.67295 11.9998 8 11.3268 8 10.4999C8 9.67295 8.67295 9 9.4999 9C10.3268 9 10.9998 9.67295 10.9998 10.4999C10.9998 11.3268 10.3268 11.9998 9.4999 11.9998ZM9.4999 9.99993C9.22492 9.99993 8.99993 10.2249 8.99993 10.4999C8.99993 10.7749 9.22492 10.9999 9.4999 10.9999C9.77488 10.9999 9.99986 10.7749 9.99986 10.4999C9.99986 10.2249 9.77488 9.99993 9.4999 9.99993Z' fill='white'/%3E%3Cpath d='M5.49952 12C5.39853 12 5.29753 11.97 5.20954 11.907C4.98456 11.746 4.93256 11.434 5.09355 11.2091L10.0932 4.20954C10.2542 3.98456 10.5662 3.93256 10.7912 4.09355C11.0161 4.25354 11.0671 4.56652 10.9071 4.7905L5.90749 11.79C5.8085 11.927 5.65551 12 5.49952 12Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0'%3E%3Crect width='16' height='16' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
    margin-right: 6px;
  }
`;

const PriceBox = styled.div`
  margin-top: 14px;
`;

const ButtonsBox = styled.div`
  display: grid;
  grid-template-columns: max-content max-content;
  grid-column-gap: 10px;
  align-items: start;
  margin-top: 20px;
  
  ${ButtonRounded} {
    padding: 21px 36px;
    font-size: 18px;
    font-weight: 500;
    line-height: 23px;
  }
  
  ${Button} {
    padding: 0;
    gap: 0;
  }
`;

const ColumnCenter = styled.div`
  text-align: center;
`;

const Installment = styled.div`
  margin-top: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({theme}) => `
    ${transparentize(.2, theme.colors.white)}
  `};
`;

// todo
//  сделать переход по кнопке записаться на курс

/**
 * The course promo main info component
 * @return {React.ReactElement}
 * @constructor
 */
export default function MainInfo(): React.ReactElement {
  const context = React.useContext<ICoursePromoContext>(CoursePromoContext);
  const {prices} = context;
  const {old, current, discountEndDate} = prices;

  const startDate = getFormattedDate(
      context.dates.start,
      DateFormat.Dot,
  );
  const endDate = getFormattedDate(
      context.dates.end,
      DateFormat.Dot,
  );
  const duration = context.dates.duration;

  const discountPercent = old && getDiscountPercent(old, current);

  /**
   * The function return course duration string
   * @return {string}
   */
  function getDuration(): string {
    if (startDate && !endDate) {
      return startDate || '';
    }

    return `
      ${startDate} - ${endDate} | ${duration} дней
    `;
  }

  /**
   * The function render course discount
   * @return {React.ReactElement | undefined}
   */
  function renderDiscount(): React.ReactElement | undefined {
    if (discountPercent) {
      const percent = discountPercent + '%';
      const endDate = getFormattedDate(discountEndDate, DateFormat.DayMonth);

      return (
        <Discount>
          Скидка {percent} до {endDate}
        </Discount>
      );
    }
  }

  /**
   * The function render course installment
   * @return {React.ReactElement | undefined}
   */
  function renderInstallment(): React.ReactElement | undefined {
    if (context?.prices?.isInstallmentAvailable) {
      return (
        <Installment>Доступна рассрочка</Installment>
      );
    }
  }

  /**
   * The function render button video
   * @return {React.ReactElement | undefined}
   */
  function renderButtonVideo(): React.ReactElement | undefined {
    if (context.preview.video) {
      return (
        <ButtonVideo/>
      );
    }
  }

  return (
    <Container>
      <Duration>
        {getDuration()}
      </Duration>
      <Title>
        {context.title}
      </Title>
      <Description>
        {context.description}
      </Description>
      {renderDiscount()}
      <PriceBox>
        <Prices />
      </PriceBox>
      <ButtonsBox>
        <ColumnCenter>
          <Button>
            <ButtonRounded>
              Записаться на курс
            </ButtonRounded>
          </Button>
          {renderInstallment()}
        </ColumnCenter>
        {renderButtonVideo()}
      </ButtonsBox>
    </Container>
  );
}
